import { useState, useEffect, Fragment, type Dispatch, type SetStateAction } from 'react'
import Select from '@/components/ui/Select'
import Menu from '@/components/ui/Menu'
import { type MenuItem } from '@/helpers/menus'
import type { App } from '@/payload-types'

type DealSidebarProps = {
  apps: App[]
  menu: MenuItem[]
  appMenus: AppMenusProps
  moreAppsMenu: MenuItem[]
  appsToShow: number
  setAppsToShow: Dispatch<SetStateAction<number>>
  isSingleAppSelection?: boolean
  secondBoxMenu?: MenuItem[]
}

export type AppMenusProps = {
  [key: string]: MenuItem[]
}

const EMPTY_BOX_KEY = '__second-app__'

export default function CompareSelect({
  apps,
  menu,
  appMenus,
  moreAppsMenu,
  appsToShow,
  setAppsToShow,
  isSingleAppSelection = false,
  secondBoxMenu = [],
}: DealSidebarProps) {
  const [dropdown, setDropdown] = useState<string | null>(null)
  const [width, setWidth] = useState<number>(0)

  useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth)
    }

    handleResize()

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    if (width <= 1024 && width > 768 && apps.length > 3) {
      setAppsToShow(2)
    } else if (width <= 768 && width > 560 && apps.length > 2) {
      setAppsToShow(1)
    } else if (width <= 560 && width > 0) {
      setAppsToShow(0)
    } else {
      setAppsToShow(apps.length)
    }
  }, [apps.length, width])

  // Single-app picker: render the chosen app, then an empty "Select app" box
  // whose menu items are links to /compare/<a>-vs-<b>.
  if (isSingleAppSelection) {
    const [appA] = apps
    return (
      <div className="flex items-center gap-3">
        {appA && (
          <Select
            menu={appMenus[appA.id] ?? []}
            label={appA.name}
            apps={[appA]}
            style={dropdown === appA.name ? 'active' : 'invert'}
            active={dropdown === appA.name}
            onClick={() => setDropdown(dropdown === appA.name ? null : appA.name)}
          />
        )}
        <span className="font-semibold text-white/40">vs</span>
        <Select
          menu={secondBoxMenu}
          label="Select app"
          apps={[]}
          style={dropdown === EMPTY_BOX_KEY ? 'active' : 'invert'}
          active={dropdown === EMPTY_BOX_KEY}
          onClick={() => setDropdown(dropdown === EMPTY_BOX_KEY ? null : EMPTY_BOX_KEY)}
          align="right"
        />
        {menu && menu.length > 0 && (
          <Menu items={menu} align="right" largeButton={true} className="dark" />
        )}
      </div>
    )
  }

  const containsAdd = menu[0]?.label === 'Add app for comparison' && menu[0].sub?.length
  const addMenu = containsAdd ? menu[0].sub : []
  const baseMenu = containsAdd ? menu.slice(2) : menu

  return (
    <div className="flex items-center gap-3">
      {apps.slice(0, appsToShow).map((app, index) => {
        return (
          <Fragment key={app.id}>
            <Select
              menu={appMenus[app.id]}
              label={app.name}
              apps={[app]}
              style={dropdown === app.name ? 'active' : 'invert'}
              active={dropdown === app.name}
              onClick={() => setDropdown(dropdown === app.name ? null : app.name)}
            />
            {index < apps.length - 1 && <span className="font-semibold text-white/40">vs</span>}
          </Fragment>
        )
      })}

      {apps.length > appsToShow && (
        <Select
          menu={moreAppsMenu}
          label={`${apps.length - appsToShow} ${appsToShow === 0 ? 'apps' : 'more'}`}
          apps={apps.slice(appsToShow)}
          style={dropdown === 'moreApps' ? 'active' : 'invert'}
          active={dropdown === 'moreApps'}
          onClick={() => setDropdown(dropdown === 'moreApps' ? null : 'moreApps')}
          align="right"
        />
      )}

      {menu && menu.length > 0 && (
        <Menu items={baseMenu} add={addMenu} align="right" largeButton={true} buttonClass="dark" />
      )}
    </div>
  )
}
